<template>
  <div>
    <div v-if="isProductVariantEnabled && !isProductOutOfStock(product)">
      <VariantForm
        :product="(product as Product)"
        @selected-variant="setSelectedVariant($event)"
      >
        <div class="add-to-cart__cta">
          <AkButton
            size="lg"
            :disabled="isButtonDisabled"
            class="button-add-to-cart"
            data-testid="addToCartButton"
            :symbol="compactAddState ? 'cart-plus' : undefined"
            @click="syncWithCart()"
          >
            <template v-if="compactAddState">
              {{ totalPriceLabel }}
            </template>
            <template v-else>
              {{ buttonLabel }}
              <span
                v-if="showTotalPrice"
                class="total-price"
              >
                {{ totalPriceLabel }}
              </span>
            </template>
          </AkButton>
        </div>
      </VariantForm>
    </div>
    <div
      v-else-if="!isProductOutOfStock(product) && !multiOptions"
      class="add-to-cart__row"
    >
      <div class="add-to-cart__qty">
        <label>
          {{ $t('Quantity') }}
          <QuantityCounter
            :model-value="quantityForCounter(product.options[0])"
            :step="product.unit_multiplier"
            :min="minForOption()"
            :max="maxForOption(product.options[0])"
            :disabled="isVariantOutOfStock(product.options[0])"
            :tracking-context="{ source: 'product_page', productId: Number(product.id), brandId: Number(product.brand?.id) || undefined }"
            data-testid="productQuantitySelect"
            @update:model-value="(v) => onQuantityChange(product.options[0].id, v)"
          />
        </label>
      </div>
      <div class="add-to-cart__cta-narrow">
        <AkButton
          size="lg"
          :disabled="isButtonDisabled"
          class="button-add-to-cart"
          data-testid="addToCartButton"
          :symbol="compactAddState ? 'cart-plus' : undefined"
          @click="syncWithCart()"
        >
          <template v-if="compactAddState">
            {{ totalPriceLabel }}
          </template>
          <template v-else>
            {{ buttonLabel }}
            <span
              v-if="showTotalPrice"
              class="total-price"
            >
              {{ totalPriceLabel }}
            </span>
          </template>
        </AkButton>
      </div>
    </div>
    <div v-else-if="!isProductOutOfStock(product)">
      <div class="options-header">
        <span>{{ $t('Options') }}</span>
        <span class="quantity">{{ $t('Quantity') }}</span>
      </div>
      <div
        v-for="option in product.options"
        :key="option.id"
      >
        <span>{{ option.variation.name }}</span>
        <QuantityCounter
          :model-value="quantityForCounter(option)"
          :step="product.unit_multiplier"
          :min="minForOption()"
          :max="maxForOption(option)"
          :disabled="isVariantOutOfStock(option)"
          :tracking-context="{ source: 'product_page', productId: Number(product.id), brandId: Number(product.brand?.id) || undefined }"
          data-testid="productQuantitySelect"
          @update:model-value="(v) => onQuantityChange(option.id, v)"
        />
      </div>
    </div>
    <AkParagraph
      v-if="isRestrictedCountry"
      class="ds-mt-3 warning"
      data-testid="restrictedCountryMessage"
    >
      {{ $t('Sorry this brand is not available in your country.') }}
    </AkParagraph>
    <AkButton
      v-if="showMultiOptionsButton"
      size="lg"
      :disabled="isButtonDisabled"
      class="button-add-to-cart"
      data-testid="addToCartButton"
      :symbol="compactAddState ? 'cart-plus' : undefined"
      @click="syncWithCart()"
    >
      <template v-if="compactAddState">
        {{ totalPriceLabel }}
      </template>
      <template v-else>
        {{ buttonLabel }}
        <span
          v-if="showTotalPrice"
          class="total-price"
        >
          {{ totalPriceLabel }}
        </span>
      </template>
    </AkButton>
    <StockAlertBtn
      v-if="isProductOutOfStock(product)"
      class="add-to-cart__stock-alert"
      btn-type="outlined"
      :is-loading="isStockAlertLoading"
      :product-variant-id="selectedVariant ? selectedVariant.id : variantId"
      :stock-alert="productVariantStockAlert"
      @set-stock-alert="createStockAlert(product.id, product.brand.id, selectedVariant?.id ?? variantId)"
    />
    <div class="ds-mt-3">
      <slot name="footer" />
    </div>
  </div>
</template>

<script lang="ts">
import { AkButton, AkParagraph } from '@ankorstore/design-system';
import { useAuthPopin } from '@core/composables/use-auth-popin';
import {
  calculateTotalPrice,
  getVariantQuantity,
  isProductOutOfStock,
  isVariantOutOfStock,
} from '@monolith/legacy/services/product-variants';
import { QuantityCounter } from '@bc/shared/components';
import { Amount } from '@core/types/amount';
import Product, { Option, PriceBreakdown, Variant } from '@monolith/legacy/types/product';
import { RetailerFormType } from '@monolith/legacy/types/retailer-form-type';
import VariantForm from '@bc/shared/components/add-to-cart/variant-form.vue';
import StockAlertBtn from '@monolith/legacy/modules/reorder/stock-status/stock-alert-btn.vue';
import { useStockStatus } from '@monolith/legacy/modules/reorder/stock-status/composables/stock-status';
import { computed, defineComponent, PropType, ref } from 'vue';
import ProductDraft from '@monolith/legacy/types/account/account-products/catalog-integration/product-integration';
import usePrice from '@core/composables/use-price';
import StoreRootHelpers from '@monolith/legacy/store/helpers';
import CartHelpers from '@monolith/legacy/store/cart/helpers';

interface AddToCartData {
  quantities: { [key: number]: number };
  vatPopinTriggered: boolean;
  disabledButton: boolean;
  saving: boolean;
  selectedVariant: Variant | undefined;
}

export default defineComponent({
  name: 'AddToCart',
  components: { QuantityCounter, VariantForm, StockAlertBtn, AkButton, AkParagraph },
  props: {
    product: {
      type: Object as PropType<Product | ProductDraft>,
      required: true,
    },
    priceBreakdown: {
      type: Object as PropType<PriceBreakdown>,
      required: true,
    },
    /** Quick-view redesign only: swaps the default "Add to cart" label for an icon + price pill. */
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['addToCart', 'done', 'openvat', 'select-variant', 'updateItemCart'],
  setup(props) {
    const { getConvertedPrice, formatPrice } = usePrice();
    const { createStockAlert, isStockAlertLoading, productVariantStockAlert } = useStockStatus();
    const { openAuthPopin } = useAuthPopin();
    const product: Product = props.product as Product;

    const totalPriceLabel = ref('');

    const { user, userCurrency, userIsRetailer, userIsBrand, canGetWholesalePrice } = StoreRootHelpers.useGetters([
      'user',
      'userCurrency',
      'userIsRetailer',
      'userIsBrand',
      'canGetWholesalePrice',
    ]);
    const { itemByOptionId, itemByVariantId } = CartHelpers.useGetters(['itemByOptionId', 'itemByVariantId']);
    const {
      addToCart: addToCartAction,
      updateItemQuantity: updateItemQuantityAction,
      deleteItem: deleteItemAction,
    } = CartHelpers.useActions(['addToCart', 'updateItemQuantity', 'deleteItem']);
    const variantId = computed(() => {
      if (product?.variants && product?.variants.length > 0) {
        return product.variants[0].id;
      }
      return null;
    });
    const showTotalPrice = computed(() => {
      return userIsRetailer.value && canGetWholesalePrice.value;
    });

    return {
      totalPriceLabel,
      user,
      userIsRetailer,
      userIsBrand,
      canGetWholesalePrice,
      itemByOptionId,
      itemByVariantId,
      createStockAlert,
      isStockAlertLoading,
      productVariantStockAlert,
      addToCartAction,
      updateItemQuantityAction,
      deleteItemAction,
      userCurrency,
      variantId,
      showTotalPrice,
      getConvertedPrice,
      formatPrice,
      openAuthPopin,
    };
  },
  data(): AddToCartData {
    return {
      quantities: {},
      vatPopinTriggered: false,
      disabledButton: true,
      saving: false,
      selectedVariant: undefined as Variant | undefined,
    };
  },
  computed: {
    multiOptions(): boolean {
      return this.getVariantsArray.length > 1;
    },
    sum(): Amount {
      return calculateTotalPrice(
        this.priceBreakdown.finalPrice,
        this.quantities[this.selectedVariant?.id],
        this.product.unit_multiplier
      );
    },
    numberOfItemsAddedToCart(): number {
      let numberOfItemsAddedToCart = 0;

      this.getVariantsArray.forEach((option) => {
        numberOfItemsAddedToCart += this.quantities[option.id] * this.product.unit_multiplier;
      });

      return numberOfItemsAddedToCart;
    },
    buttonLabel(): string {
      if (this.user && !this.userIsRetailer) {
        return this.$t('Only retailers can shop');
      }
      if (this.isProductVariantEnabled && !this.selectedVariant) {
        return this.$t('Choose an option');
      }
      if (this.sum.amount === 0) {
        return this.alreadyInCart() ? this.$t('Remove from cart') : this.$t('Select quantities');
      }

      if (this.saving) {
        return this.$tc('Item added to cart! | Item added to cart! | Items added to cart!', this.numberOfItemsAddedToCart);
      }

      if (this.alreadyInCart()) {
        return this.$t('Update cart');
      }
      return this.$t('Add to cart');
    },
    getVariantsArray(): Variant[] | Option[] {
      return this.isProductVariantEnabled ? this.product.variants : this.product.options;
    },
    isProductVariantEnabled(): boolean {
      return this.product?.brand?.is_product_variants_enabled;
    },
    isButtonDisabled(): boolean {
      return (
        (this.user && !this.userIsRetailer) ||
        (this.sum.amount === 0 && !this.alreadyInCart()) ||
        (this.isProductVariantEnabled && !this.selectedVariant) ||
        this.isRestrictedCountry
      );
    },
    isRestrictedCountry() {
      return !this.product.brand.opened_countries.includes(this.user?.business?.country.iso_code);
    },
    showMultiOptionsButton(): boolean {
      return !isProductOutOfStock(this.product) && !this.isProductVariantEnabled && this.multiOptions;
    },
    // Mirrors the fallthrough in buttonLabel() that resolves to the plain "Add to cart" text.
    isDefaultAddState(): boolean {
      return (
        !(this.user && !this.userIsRetailer) &&
        !(this.isProductVariantEnabled && !this.selectedVariant) &&
        this.sum.amount !== 0 &&
        !this.saving &&
        !this.alreadyInCart()
      );
    },
    compactAddState(): boolean {
      return this.compact && this.isDefaultAddState && this.showTotalPrice;
    },
  },
  watch: {
    async sum(newValue): Promise<void> {
      this.totalPriceLabel = newValue.amount === 0 ? '' : this.formatPrice(await this.getConvertedPrice(newValue));
    },
    canGetWholesalePrice(newValue): void {
      if (newValue) {
        // without that test, the syncWithCart is called even when we entered the vat to see the wholesale price
        // it was making a weird behavior like adding an item to the cart when you just wanted to see the wholesale price.
        if (this.vatPopinTriggered) {
          this.syncWithCart();
        }
      }
    },
  },
  created(): void {
    const defaultQuantity = this.multiOptions ? 0 : 1;
    this.getVariantsArray.forEach((option) => {
      const item = this.getItemForOption(option.id);
      this.quantities[option.id] = item ? item.quantity : defaultQuantity;
    });
    this.lastQuantities = this.quantities;
  },
  methods: {
    toggleClass(element: Element, classname: string): void {
      element.classList.toggle(classname);
      this.saving = true;
      setTimeout(() => {
        this.saving = false;
        element.classList.toggle(classname);
      }, 3000);
    },
    getItemForOption(id: number) {
      return this.isProductVariantEnabled ? this.itemByVariantId(id) : this.itemByOptionId(id);
    },
    quantityForCounter(option: Option): number {
      return (this.quantities[option.id] || 0) * this.product.unit_multiplier;
    },
    minForOption(): number {
      return this.multiOptions || this.alreadyInCart() ? 0 : this.product.unit_multiplier;
    },
    maxForOption(option: Option): number | undefined {
      if (isVariantOutOfStock(option)) {
        return 0;
      }
      const qty = getVariantQuantity(option);
      if (!qty || qty < 0) {
        return undefined;
      }
      return qty;
    },
    onQuantityChange(optionId: number, unitValue: number): void {
      this.quantities = Object.assign({}, this.quantities, {
        [optionId]: unitValue / this.product.unit_multiplier,
      });
    },
    syncWithCart(): void {
      if (!this.user) {
        this.openAuthPopin(RetailerFormType.Register);
        return;
      }

      if (!this.canGetWholesalePrice) {
        this.vatPopinTriggered = true;
        this.$emit('openvat');
        return;
      }

      if (!this.product.refreshed) {
        alert('Product is not refreshed');
        return;
      }

      const buttonAddToCart = document.querySelector('.add-to-cart .button-add-to-cart');

      this.getVariantsArray.forEach((item) => {
        const quantity = parseInt(this.quantities[item.id]?.toString());
        const cartItem = this.getItemForOption(item.id);

        if (quantity && cartItem && quantity === cartItem.quantity) {
          return;
        }
        if (cartItem) {
          if (quantity === 0) {
            this.deleteItem(cartItem);
          } else {
            this.updateItem(cartItem, quantity, buttonAddToCart);
          }
        } else if (quantity > 0) {
          this.addToCart(item, quantity, buttonAddToCart);
        }

        this.lastQuantities = this.quantities;
        this.$emit('done');
      });
    },
    async addToCart(item: Option, quantity: number, buttonAddToCart: Element): Promise<void> {
      this.toggleClass(buttonAddToCart, 'added');
      const payload = {
        brand: this.product.brand,
        option: item,
        variant: null,
        quantity,
      };
      if (this.isProductVariantEnabled && this.selectedVariant) {
        payload.variant = item;
        payload.option = this.product.options.find(({ sku }) => sku === item.sku);
      }
      await this.addToCartAction(payload);
      this.$emit('addToCart', payload);
    },
    updateItem(item: Option, quantity: number, buttonAddToCart: Element): void {
      this.toggleClass(buttonAddToCart, 'added');

      // `quantity` is already the absolute total the user picked (seeded from item.quantity,
      // then overwritten by onQuantityChange) - not a delta, so it's sent as-is.
      this.updateItemQuantityAction({
        brand: this.product.brand,
        item,
        quantity,
      }).then(() => {});
      this.$emit('updateItemCart', { item, quantity });
    },
    deleteItem(item): void {
      this.deleteItemAction({
        brand: this.product.brand,
        item: item,
      });
      // reset select value to 1 for only 1 option after deletedItem
      if (!this.multiOptions) {
        this.quantities[this.product.options[0].id] = 1;
      }
    },
    alreadyInCart(): boolean {
      if (
        this.isProductVariantEnabled &&
        this.selectedVariant &&
        this.itemByVariantId(this.selectedVariant.id)
      ) {
        return true;
      }
      for (const option of this.getVariantsArray) {
        if (this.itemByOptionId(option.id)) {
          return true;
        }
      }
      return false;
    },
    setSelectedVariant(selected: { variant: Variant; quantity: number }): void {
      // reset the others value to cart value
      this.getVariantsArray.forEach((option) => {
        const item = this.itemByVariantId(option.id);
        this.quantities = Object.assign({}, this.quantities, {
          [option.id]: item ? item.quantity : 0,
        });
      });
      // set new variant and quantity
      this.selectedVariant = selected.variant;
      if (selected.variant) {
        this.quantities = Object.assign({}, this.quantities, {
          [selected.variant.id]: selected.quantity,
        });
      }
      this.$emit('select-variant', this.selectedVariant);
    },
    isVariantOutOfStock,
    isProductOutOfStock,
  },
});
</script>

<style scoped lang="scss">
@import '@css/vue-import';

select {
  width: 100%;
}
button {
  width: 100%;
}

label,
.options-header {
  @apply ds-text-neutral-700;
  position: relative;
  font-size: 14px;
  width: 100%;
  span.quantity {
    position: absolute;
    right: 0;
  }
}

.warning {
  color: rgba(255, 100, 0, var(--tw-text-opacity));
}

.button {
  height: 50px;

  span {
    display: inline-block;
    margin-left: 8px;
  }
}

.button-add-to-cart {
  @apply ds-text-base ds-w-full ds-justify-center;
}

.total-price {
  @apply ds-ml-1;
}

.add-to-cart {
  &__row {
    @apply ds-flex ds-flex-col ds-gap-2 lg:ds-flex-row lg:ds-items-end;
  }

  &__qty {
    @apply lg:ds-flex-1;
  }

  &__cta {
    @apply lg:ds-flex-1;
  }

  &__cta-narrow {
    @apply lg:ds-w-1/2;
  }

  &__stock-alert {
    @apply ds-top-2;
  }
}

button.added {
  i {
    opacity: 1;
    transition: opacity 1s linear;
  }

  .total-price {
    display: none;
  }
}

.button-black:active,
.button-black:hover,
.button-black:focus {
  @apply ds-bg-primary ds-text-white;
  text-decoration: none;
}

.button-black:disabled:active,
.button-black:disabled:hover,
.button-black:disabled:focus {
  @apply ds-text-neutral-300;
  @apply ds-bg-neutral-900;
}
</style>
